<template lang="pug">
    div(class="material-effects")
        label(
            v-if="label",
            class="label"
            :class="{'-required': isRequired,'-optional': !isRequired}"
        ) {{ label }}

        CheckIcon(
            v-else-if="!disablePosValidation && isDirty"
            class="icon -success -right"
        )

        span(class="bar")

        span(v-if="error" class="message-block")
            span(class="localError")
                | Error: {{ error }}
</template>

<script>
/**
 * A wrapper component for rendering a field in Material Design
 * Can take any Input/Select/etc and Slot it in.
 */
const CheckIcon = () =>
    import(/* webpackChunkName: "check-icon" */ '@/assets/common/checkmarkThin?inline')

export default {
    name: 'Material',
    components: {
        CheckIcon,
    },
    props: {
        label: {
            type: String,
            required: false,
            default: null,
        },
        // Is this input required or optional?
        isRequired: {
            type: Boolean,
            default: false,
        },
        disablePosValidation: {
            type: Boolean,
            default: false,
        },
        error: {
            type: String,
            required: false,
            default: null,
        },
        isDirty: {
            type: Boolean,
            default: false,
        },
    },
}
</script>

<style lang="stylus">
_horizontalSpace = 16
_verticalSpace = 12
_topPadding = 15
form
    &.-dark
        .material-input
            .baseInput
            .baseTextarea
            .baseSelect
                background-color: theme('colors.gray.lightest')
            &:focus
            &:active
                background-color: theme('colors.gray.lightest')
    .material-input
        padding initial
        padding-bottom 10px
        .label
                color: theme('colors.gray.dark')
        .icon
            &.-danger
                fill: theme('colors.danger.DEFAULT')
        // Error messages
        .message-block
            color: theme('colors.danger.DEFAULT')
        .baseInput
        .baseTextarea
        .baseSelect
            background-color: theme('colors.white')
            color: theme('colors.black')
            border: none;
            border-bottom: solid 1px theme('colors.gray.light')
            border-radius: 4px
            border-bottom-left-radius: 0
            border-bottom-right-radius: 0
            box-shadow: none
            padding: 0 (_horizontalSpace)px
            height: 50px
            &.-hasLabel
                padding-top (_topPadding + 2)px // Text needs to be under label
                padding-bottom (_verticalSpace)px
                font-size theme('fontSize.sm')
            &:hover
                background-color: theme('colors.gray.lighter')
                border-bottom-color: theme('colors.gray.DEFAULT')
            &:focus
            &:active
                background-color: theme('colors.white')
                outline: none
                & ~ .material-effects .bar:before
                & ~ .material-effects .bar:after
                    background: theme('colors.action.DEFAULT')
                    width: 50%
                & ~ .material-effects .label
                        color: theme('colors.action.DEFAULT')
            // Label when not in focus (grow in size - center)
            &:not(:focus):placeholder-shown ~ .material-effects .label // For inputs
            &:not(:focus).-noSelection ~ .material-effects .label // For Select Boxes
                color: theme('colors.gray.dark')
                left (_horizontalSpace)px
                top (_topPadding + 10)px
                // font-weight $bold
                font-size theme('fontSize.sm')
            &.-hasError // Modify color scheme when in Error state
                border-bottom-color: theme('colors.danger.DEFAULT')
                & ~ .material-effects .bar:before
                & ~ .material-effects .bar:after
                    background: theme('colors.danger.DEFAULT')
                & ~ .material-effects .label
                    color: theme('colors.danger.DEFAULT')
            // Label when not in focus (grow in size - center)
            &:not(:focus):placeholder-shown ~ .material-effects .icon
                top (_topPadding)px
            // Overrides when Disabled
            &.-disabled
                background-color: theme('colors.gray.lightest')
                border-bottom-color: theme('colors.gray.light')
                color: theme('colors.gray.light')
                & ~ .material-effects .label
                    color: theme('colors.gray.light')
                &:hover
                    background-color: theme('colors.gray.lightest')
                    border-bottom-color: theme('colors.gray.light')
                &:focus
                &:active
                    background-color: theme('colors.gray.lightest')
                    & ~ .material-effects .label
                        color: theme('colors.gray.light')
                &:active
                    & ~ .material-effects .bar:before
                    & ~ .material-effects .bar:after
                        width: 0
                // Label when not in focus (grow in size - center)
                &:not(:focus):placeholder-shown ~ .material-effects .label // For inputs
                &:not(:focus).-noSelection ~ .material-effects .label // For Select Boxes
                    color: theme('colors.gray.light')
            &::placeholder
                color: theme('colors.gray.DEFAULT')
                text-align left
            &.-hasLabel:not(:focus)::placeholder
                color transparent !important
            &[type="number"]
                -moz-appearance:textfield
            &[type="number"]::-webkit-outer-spin-button,
            &[type="number"]::-webkit-inner-spin-button
                -webkit-appearance: none
                margin: 0

        .material-effects
            line-height: 0
            .bar // Serves as a bottom border to the input
                position:relative
                top: 1px
                display:block
                width: 100%
                &:before
                &:after
                    content:''
                    height: 2px
                    width: 0
                    bottom:1px
                    position: absolute
                    background: _inputBB
                    transition:0.2s ease all
                &:before
                    left:50%
                &:after
                    right:50%
                // Default Label styles are for when it is small

            .label
                position: absolute
                top: (_verticalSpace)px
                left: (_horizontalSpace)px
                font-weight: theme('fontWeight.normal')
                font-size: theme('fontSize.xs')
                pointer-events: none
                transition: 0.2s ease all
                z-index: theme('zIndex.10')
                &.-required:after
                    content: " *" // You are welcome Josh!!
                    font-size: theme('fontSize.xs')
                &.-optional:after
                    content: " (Optional)" // So handy!!
                    font-size: theme('fontSize.xs')
            .icon
                position: absolute
                top: (_topPadding)px
                right: (_horizontalSpace)px
                z-index: theme('zIndex.10')
                &.-success
                    fill: theme('colors.success.DEFAULT')
                    width: 20px
                &.-right
                    right: 10px
                // Error messages
            .message-block
                display: block
                margin-left: (_horizontalSpace)px
                font-size: theme('fontSize.xs')
                height: (_verticalSpace)px
                position: relative
                top: 8px
                white-space: nowrap
</style>
